import { useState } from 'react';
import { contact } from '../data';
import s from './Contact.module.css';

// ─────────────────────────────────────────────────────────────────
//  Web3Forms — free, no backend needed
//  1. Go to https://web3forms.com
//  2. Enter info@nextitera.com → click "Create Access Key"
//  3. Paste the key below
// ─────────────────────────────────────────────────────────────────
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || 'YOUR_ACCESS_KEY_HERE';

const SERVICES = [
  'Mobile App', 'Web App', 'UI/UX Design',
  'E-Commerce', 'Cloud & DevOps', 'Something else',
];

const INFO = [
  { icon: '📞', label: 'Phone',    val: contact.phone    },
  { icon: '✉️', label: 'Email',    val: contact.email    },
  { icon: '📍', label: 'Location', val: contact.location },
];

export default function Contact() {
  const [form,   setForm]   = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key:   ACCESS_KEY,
          subject:      `New project inquiry from ${form.name}`,
          from_name:    form.name,
          email:        form.email,
          phone:        form.phone    || 'Not provided',
          service:      form.service  || 'Not specified',
          message:      form.message,
          botcheck:     '',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setStatus('sent');
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle');
    setForm({ name: '', email: '', phone: '', service: '', message: '' });
  };

  return (
    <section id="contact" className="dark-section" aria-label="Contact">
      <div className="section-inner">
        <div className={s.wrap}>
          <div className={s.grid}>

            {/* ── Info col ── */}
            <div>
              <div className="kicker">
                <div className="kicker-line-dark" />
                <span className="kicker-text-dark">Get In Touch</span>
              </div>
              <h2 className={`section-h ${s.heading}`}>Let's Build<br />Something Great</h2>
              <p className={s.p}>
                Drop us a message — no sales pitch, just a friendly chat about your project.
              </p>

              <div className={s.items}>
                {INFO.map(({ icon, label, val }) => (
                  <div key={label} className={s.item}>
                    <div className={s.itemIcon}>{icon}</div>
                    <div>
                      <div className={s.itemLabel}>{label}</div>
                      <div className={s.itemVal}>{val}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className={s.badge}>
                <span className={s.badgeDot} />
                <span>Reply within <strong>24 hours</strong></span>
              </div>
            </div>

            {/* ── Form col ── */}
            <div>
              {status === 'sent' ? (
                <div className={s.success}>
                  <div className={s.successIcon}>✅</div>
                  <h3 className={s.successTitle}>Message Sent!</h3>
                  <p className={s.successSub}>
                    Your message was delivered to <strong>info@nextitera.com</strong>.<br />
                    We'll reply within 24 hours 🙌
                  </p>
                  <button className={s.resetBtn} onClick={reset}>
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className={s.form}>
                  {/* Honeypot — stops spam */}
                  <input type="checkbox" name="botcheck" style={{ display: 'none' }} />

                  <div className={s.row2}>
                    <div className={s.field}>
                      <label className={s.label}>Name *</label>
                      <input
                        required
                        value={form.name}
                        onChange={e => set('name', e.target.value)}
                        placeholder="Your name"
                        className={s.input}
                      />
                    </div>
                    <div className={s.field}>
                      <label className={s.label}>Email *</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={e => set('email', e.target.value)}
                        placeholder="you@company.com"
                        className={s.input}
                      />
                    </div>
                  </div>

                  <div className={s.field}>
                    <label className={s.label}>Phone</label>
                    <input
                      value={form.phone}
                      onChange={e => set('phone', e.target.value)}
                      placeholder="+971 ..."
                      className={s.input}
                    />
                  </div>

                  <div className={s.field}>
                    <label className={s.label}>Service needed</label>
                    <select
                      value={form.service}
                      onChange={e => set('service', e.target.value)}
                      className={s.input}
                    >
                      <option value="">Pick a service</option>
                      {SERVICES.map(sv => <option key={sv}>{sv}</option>)}
                    </select>
                  </div>

                  <div className={s.field}>
                    <label className={s.label}>About your project *</label>
                    <textarea
                      required
                      rows={4}
                      value={form.message}
                      onChange={e => set('message', e.target.value)}
                      placeholder="Tell us what you're building — any detail helps!"
                      className={s.input}
                    />
                  </div>

                  {status === 'error' && (
                    <div className={s.errorBox}>
                      ⚠️ Something went wrong. Please email us directly at{' '}
                      <a href="mailto:info@nextitera.com">info@nextitera.com</a>
                    </div>
                  )}

                  <button
                    type="submit"
                    className={s.submit}
                    disabled={status === 'sending'}
                  >
                    {status === 'sending'
                      ? <span className={s.sendingRow}><span className={s.spinner} /> Sending...</span>
                      : 'Send Message →'
                    }
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
